import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase/config';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { useToast } from './ToastContext';

const Notes = ({ userId, userName }) => {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const { showSuccess, showError } = useToast();

  // Clear all existing notes from Firebase
  const clearAllNotes = async () => {
    try {
      console.log('Clearing all existing notes...');
      const q = query(collection(firestore, 'notes'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log(`Deleted ${querySnapshot.docs.length} existing notes`);
      setNotes([]);
      showSuccess('All previous notes cleared successfully!');
    } catch (error) {
      console.error('Error clearing notes:', error);
      showError('Failed to clear notes: ' + error.message);
    }
  };

  // Load notes from Firebase
  const loadNotes = async () => {
    if (!userId) return;
    
    try {
      setNotesLoading(true);
      console.log('Loading notes for user:', userId);
      
      // Simplified query without orderBy to avoid index requirement
      const q = query(
        collection(firestore, 'notes'),
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      const notesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort notes by creation date in JavaScript instead of Firestore
      const sortedNotes = notesData.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      console.log('Loaded notes:', sortedNotes);
      setNotes(sortedNotes);
    } catch (error) {
      console.error('Error loading notes:', error);
      showError('Failed to load notes: ' + error.message);
    } finally {
      setNotesLoading(false);
    }
  };

  // Save new note to Firebase
  const saveNote = async () => {
    if (!title.trim() || !description.trim()) {
      showError('Please fill in both title and description');
      return;
    }

    if (!userId) {
      showError('User ID is required');
      return;
    }

    setLoading(true);
    try {
      const noteData = {
        title: title.trim(),
        description: description.trim(),
        userId,
        userName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Saving new note:', noteData);
      
      const docRef = await addDoc(collection(firestore, 'notes'), noteData);
      console.log('Note saved with ID:', docRef.id);
      
      // Add to local state
      const newNote = { id: docRef.id, ...noteData };
      setNotes(prev => [newNote, ...prev]);
      
      // Clear form
      setTitle('');
      setDescription('');
      
      showSuccess('Note saved successfully!');
    } catch (error) {
      console.error('Error saving note:', error);
      showError('Failed to save note: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Update existing note
  const updateNote = async () => {
    if (!editingNote) return;
    
    setLoading(true);
    try {
      const updatedData = {
        title: title.trim(),
        description: description.trim(),
        updatedAt: new Date().toISOString()
      };

      console.log('Updating note:', editingNote.id, updatedData);
      
      await updateDoc(doc(firestore, 'notes', editingNote.id), updatedData);
      
      // Update local state
      setNotes(prev => prev.map(note => 
        note.id === editingNote.id 
          ? { ...note, ...updatedData }
          : note
      ));
      
      // Clear form
      setTitle('');
      setDescription('');
      setEditingNote(null);
      
      showSuccess('Note updated successfully!');
    } catch (error) {
      console.error('Error updating note:', error);
      showError('Failed to update note: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation
  const showDeleteConfirmation = (note) => {
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  // Delete note
  const deleteNote = async () => {
    if (!noteToDelete) return;

    try {
      console.log('Deleting note:', noteToDelete.id);
      
      await deleteDoc(doc(firestore, 'notes', noteToDelete.id));
      
      // Remove from local state
      setNotes(prev => prev.filter(note => note.id !== noteToDelete.id));
      
      showSuccess('Note deleted successfully!');
    } catch (error) {
      console.error('Error deleting note:', error);
      showError('Failed to delete note: ' + error.message);
    } finally {
      setShowDeleteConfirm(false);
      setNoteToDelete(null);
    }
  };

  // Cancel delete confirmation
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setNoteToDelete(null);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingNote) {
      await updateNote();
    } else {
      await saveNote();
    }
  };

  // Handle edit
  const handleEdit = (note) => {
    setTitle(note.title);
    setDescription(note.description);
    setEditingNote(note);
  };

  // Handle cancel edit
  const handleCancel = () => {
    setTitle('');
    setDescription('');
    setEditingNote(null);
  };

  // Handle note click to open modal
  const handleNoteClick = (note) => {
    setSelectedNote(note);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedNote(null);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Load notes on component mount
  useEffect(() => {
    if (userId) {
      loadNotes();
    }
  }, [userId]);

  return (
    <div className="bg-white/80 rounded-2xl shadow-xl p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        Class Notes
      </h3>

      {/* Note Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="space-y-4">
          <div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Note title..."
              className="w-full px-0 py-2 border-b-2 border-gray-300 focus:border-blue-500 bg-transparent outline-none transition-colors text-lg"
              maxLength={100}
            />
          </div>
          <div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Write your important points here..."
              rows="4"
              className="w-full px-0 py-2 border-b-2 border-gray-300 focus:border-blue-500 bg-transparent outline-none transition-colors resize-none"
              maxLength={1000}
            />
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !title.trim() || !description.trim()}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {editingNote ? 'Updating...' : 'Saving...'}
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {editingNote ? 'Update Note' : 'Save Note'}
                </div>
              )}
            </button>
            {editingNote && (
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-all duration-200 shadow-lg"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Saved Notes */}
      <div>
        <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center">
          <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Saved Notes ({notes.length})
        </h4>

        {notesLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading notes...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <p>No notes yet. Start taking notes during your classes!</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {notes.map((note) => (
              <div 
                key={note.id} 
                className="bg-white rounded-lg shadow-md border border-gray-200 p-4 cursor-pointer hover:shadow-lg transition-shadow w-[280px] flex-shrink-0"
                onClick={() => handleNoteClick(note)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-semibold text-gray-800 text-sm truncate flex-1">{note.title}</h5>
                  <div className="flex gap-1 ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(note);
                      }}
                      className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                      title="Edit note"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        showDeleteConfirmation(note);
                      }}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                      title="Delete note"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                <p className="text-gray-600 text-xs line-clamp-3 mb-2">{note.description}</p>
                <div className="text-xs text-gray-400">
                  Created: {formatDate(note.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for viewing note details */}
      {showModal && selectedNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-800">{selectedNote.title}</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="mb-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedNote.description}</p>
              </div>
              <div className="text-sm text-gray-500 border-t pt-4">
                Created: {formatDate(selectedNote.createdAt)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && noteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">Delete Note</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Note Title:</span> {noteToDelete.title}
                </p>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {noteToDelete.description}
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteNote}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors font-medium flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes; 